import type { Tone } from "@/components/ui";
import type {
  Deployment,
  DeploymentEvent,
  DeploymentStatus,
} from "@/types/api";

// Steps the deploy workflow emits an event for. Order matters: it lays out the
// timeline even when only a subset of events has landed yet (the GUI polls
// while the run is still in flight). Keep this list in lock-step with
// scripts/notify_progress.py callers in deploy-tenant.yml.
export const STEP_ORDER: string[] = [
  "fetch_lambdas",
  "fetch_gui",
  "materialize_settings",
  "configure_tenant_creds",
  "preflight_bootstrap",
  "cdk_deploy",
  "smoke_test",
];

// Operator-facing step labels — strictly business vocabulary, no internal
// tooling names (CDK / GHA / lambda / pool) leak into the UI.
export const STEP_LABEL: Record<string, string> = {
  fetch_lambdas: "Fetching components",
  fetch_gui: "Fetching interface",
  materialize_settings: "Preparing configuration",
  configure_tenant_creds: "Connecting to environment",
  preflight_bootstrap: "Preparation",
  cdk_deploy: "Setting up infrastructure",
  smoke_test: "Final check",
};

// Polling cadence while a run is still in flight. Callers pair this with
// ``refetchIntervalInBackground: false`` so a page left open overnight doesn't
// keep hammering the API from a hidden tab.
export const POLL_INTERVAL_MS = 5000;

export function isTerminalStatus(s: DeploymentStatus): boolean {
  return s === "success" || s === "failed" || s === "partial_failure";
}

export function isActiveStatus(s: DeploymentStatus): boolean {
  return s === "triggered" || s === "in_progress";
}

/** True when at least one deployment in the set is still running. */
export function hasInflight(items: Deployment[]): boolean {
  return items.some((d) => !isTerminalStatus(d.status));
}

export function inflightCount(items: Deployment[]): number {
  return items.filter((d) => !isTerminalStatus(d.status)).length;
}

/** Most recent deployment per stage (by trigger time), for the env cards. */
export function latestDeploymentByStage(
  items: Deployment[],
): Map<string, Deployment> {
  const m = new Map<string, Deployment>();
  for (const d of items) {
    const existing = m.get(d.stage);
    if (!existing || d.triggeredAt > existing.triggeredAt) m.set(d.stage, d);
  }
  return m;
}

export function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m} min ${rem.toString().padStart(2, "0")} s`;
}

export function totalDuration(d: Deployment): string {
  if (d.completedAt) return formatDuration(d.completedAt - d.triggeredAt);
  if (isTerminalStatus(d.status)) return "—";
  return `${formatDuration(Date.now() - d.triggeredAt)} (in progress)`;
}

/** What a status dot looks like: a tone and whether it pulses. */
export interface DotSpec {
  tone: Tone;
  pulse: boolean;
}

/** Status dot for a whole deployment (task feed / tree pills). Active runs
 *  pulse. Same tones as the DeploymentStatusBadge, except `triggered`: the pill
 *  says the run is queued (neutral), the dot says it is about to move (info,
 *  pulsing) — both have always differed. */
export function deploymentDot(status: DeploymentStatus): DotSpec {
  switch (status) {
    case "success":
      return { tone: "success", pulse: false };
    case "failed":
      return { tone: "danger", pulse: false };
    case "partial_failure":
      return { tone: "warning", pulse: false };
    case "triggered":
    case "in_progress":
      return { tone: "info", pulse: true };
    case "unknown":
    default:
      return { tone: "neutral", pulse: false };
  }
}

export function eventDot(status: DeploymentEvent["status"]): DotSpec {
  switch (status) {
    case "succeeded":
      return { tone: "success", pulse: false };
    case "failed":
      return { tone: "danger", pulse: false };
    case "skipped":
      return { tone: "neutral", pulse: false };
    case "started":
    default:
      return { tone: "info", pulse: true };
  }
}
